import { Play, Clock, Users, Star, Lock } from "lucide-react";
import { Link } from "react-router-dom";

interface CourseCardProps {
  id: string;
  title: string;
  instructor?: string;
  thumbnail: string;
  duration: string;
  students: number;
  rating: number;
  category: string;
  isLocked?: boolean;
}

const CourseCard = ({ id, title, instructor, thumbnail, duration, students, rating, category, isLocked = false }: CourseCardProps) => {
  const cardContent = (
    <div className="bg-card rounded-xl overflow-hidden shadow-card hover:shadow-elevated transition-all duration-300 hover:-translate-y-1">
      {/* Thumbnail */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <img 
          src={thumbnail} 
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full bg-primary-foreground/90 flex items-center justify-center">
            {isLocked ? (
              <Lock className="w-3.5 h-3.5 text-primary" />
            ) : (
              <Play className="w-3.5 h-3.5 text-primary ml-0.5" fill="currentColor" />
            )}
          </div>
        </div>
        <div className="absolute top-2 left-2">
          <span className="px-1.5 py-0.5 rounded-full text-[8px] font-semibold bg-primary text-primary-foreground">
            {category}
          </span>
        </div>
        {isLocked && (
          <div className="absolute top-2 right-2">
            <span className="px-1.5 py-0.5 rounded-full text-[8px] font-semibold bg-foreground/80 text-primary-foreground flex items-center gap-0.5">
              <Lock className="w-2 h-2" />
              Locked
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-2.5">
        <h3 className="font-display text-xs font-semibold text-foreground mb-0.5 line-clamp-1 group-hover:text-primary transition-colors">
          {title}
        </h3>
        {instructor && (
          <p className="text-[10px] text-muted-foreground mb-2">by {instructor}</p>
        )}

        {/* Meta */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <div className="flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5" />
              <span>{duration}</span>
            </div>
            <div className="flex items-center gap-0.5">
              <Users className="w-2.5 h-2.5" />
              <span>{students.toLocaleString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            <Star className="w-2.5 h-2.5 text-accent" fill="currentColor" />
            <span className="text-[10px] font-semibold text-foreground">{rating.toFixed(1)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLocked) {
    return <div className="group block">{cardContent}</div>;
  }

  return (
    <Link to={`/courses/${id}`} className="group block">
      {cardContent}
    </Link>
  );
};

export default CourseCard;
